import { createContext, useContext, useMemo, useCallback } from 'react';
import usePerformanceTracking from 'framework-listing-lib/internal/hooks/usePerformanceTracking';
import { MEASURE_MARK_NAMES } from 'framework-listing-lib/internal/utils/performanceTracking';
import useParseTableColumns from '../hooks/useParseTableColumns';
import { jsx as _jsx } from "react/jsx-runtime";
const TableColumnsContext = /*#__PURE__*/createContext(undefined);
TableColumnsContext.displayName = 'TableColumnsContext';

/**
 * Used by class components.
 */
export const TableColumnsContextConsumer = TableColumnsContext.Consumer;
export default function TableColumnsProvider({
  children,
  columnKeys,
  propertyMap,
  renderCustomColumn,
  total
}) {
  usePerformanceTracking(MEASURE_MARK_NAMES.TABLE_COLUMNS);
  const {
    loading,
    tableColumns,
    updateColumnConfiguration,
    updateColumnWidth
  } = useParseTableColumns({
    columnKeys,
    propertyMap,
    renderCustomColumn,
    total
  });
  const onResize = useCallback((columnId, width) => {
    updateColumnWidth(columnId, width);
  }, [updateColumnWidth]);
  const onResizeEnd = useCallback((columnId, width) => {
    const newConfig = tableColumns.reduce((acc, col) => {
      acc[col.id] = {
        width: col.id === columnId ? width : col.width
      };
      return acc;
    }, {});
    updateColumnConfiguration(newConfig);
  }, [tableColumns, updateColumnConfiguration]);
  const value = useMemo(() => ({
    loading,
    onResize,
    onResizeEnd,
    tableColumns
  }), [loading, onResize, onResizeEnd, tableColumns]);
  return /*#__PURE__*/_jsx(TableColumnsContext.Provider, {
    value: value,
    children: children
  });
}
export function useTableColumns() {
  const context = useContext(TableColumnsContext);
  if (context === undefined) {
    throw new Error('useTableColumns must be used within a TableColumnsProvider');
  }
  return context;
}
export function useTableColumn(propertyName) {
  const {
    tableColumns
  } = useTableColumns();
  return tableColumns.find(col => col.id === propertyName);
}